/**
 * CodeEditorView Component
 *
 * Main view for AI-assisted code transformation.
 * Three-panel layout with section navigator, source code, and target code.
 * Right-click on code sections to access AI features via context menu.
 */

import { useState, useCallback, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { Allotment } from 'allotment'
import Editor from '@monaco-editor/react'
import {
  ArrowLeft, Save, RotateCcw, FileCode, FileCode2, PanelLeftClose, PanelLeft, ChevronDown, Loader2, Download, Wand2
} from 'lucide-react'
import SectionNavigator from '../editor/SectionNavigator'
import AIResponseModal from '../editor/AIResponseModal'
import CleanCodeModal from '../editor/CleanCodeModal'
import { useApplications, useCodeEditorFiles, useSettings } from '../../hooks'
import {
  generateJavaFromCobol,
  explainCobolCode,
  optimizeJavaCode,
  customPrompt as customPromptAI,
  cleanCode
} from '../../services/aiService'

import 'allotment/dist/style.css'
import './CodeEditorView.css'

// Set to true to enable verbose Code Refactor pipeline logging in browser console
const DEBUG_REFACTOR = false

/**
 * CodeEditorView
 *
 * Accepts two optional props for embedded use inside ResolveIssuesView:
 *
 *   hideHeader={true}
 *     Suppress the top toolbar (Back / Download / Save buttons).
 *     The parent view provides its own navigation.
 *
 *   externalNavTarget={{ cobolFile, javaFile, startLine }}
 *     When this prop changes, the editor switches to the specified files and
 *     scrolls to startLine. Used by ResolveIssuesView to navigate to an issue.
 *
 * A ref can be forwarded to call triggerAiAction(action) programmatically,
 * which fires the same handler as the Monaco command palette AI actions.
 *
 * Reuse docs:
 *   - Editor components: CodeEditorView.jsx, SectionNavigator.jsx, AIResponseModal.jsx, CleanCodeModal.jsx
 *   - AI actions wired via Monaco addAction() → handleEditorAiAction
 *   - File I/O: useCodeEditorFiles hook → /editor/bundle, /editor/cobol/file, /editor/java/file
 */
// ── computeChangedRanges ─────────────────────────────────────────────────────
// Pure helper — line-by-line diff of two content strings.
// Returns array of { startLine, endLine, originalLines, newLines } (all 1-indexed).
function computeChangedRanges(prevContent, newContent) {
  const prevLines = prevContent.split('\n')
  const newLines  = newContent.split('\n')
  const ranges = []
  let i = 0
  const maxLen = Math.max(prevLines.length, newLines.length)
  while (i < maxLen) {
    if (prevLines[i] !== newLines[i]) {
      let j = i + 1
      while (j < maxLen && prevLines[j] !== newLines[j]) j++
      ranges.push({
        startLine:     i + 1,
        endLine:       j,
        originalLines: prevLines.slice(i, j),
        newLines:      newLines.slice(i, j),
      })
      i = j
    } else { i++ }
  }
  return ranges
}

// MZ: Fix Panel commands registered in the Java editor command palette.
// contextMenu:true → also shown in the right-click context menu (all others palette-only).
const MZ_PALETTE_COMMANDS = [
  { id: 'mz-ai-repair-method',         label: 'MZ: AI Repair Method',         key: 'ai-repair-method'         },
  { id: 'mz-ai-repair-region',         label: 'MZ: AI Repair Region',         key: 'ai-repair-region'         },
  { id: 'mz-ai-repair-file-structure', label: 'MZ: AI Repair File Structure', key: 'ai-repair-file-structure' },
  { id: 'mz-ai-safe-patch',            label: 'MZ: AI Fix (Safe Patch)',       key: 'ai-safe-patch'            },
  { id: 'mz-apply-stub',               label: 'MZ: Apply as Stub',             key: 'apply-stub'               },
  { id: 'mz-comment-out-line',         label: 'MZ: Comment Out Line',          key: 'comment-out-line'         },
  { id: 'mz-block-comment',            label: 'MZ: Block Comment Selection',   key: 'block-comment'            },
  { id: 'mz-save-file',                label: 'MZ: Save File',                 key: 'save-file',   contextMenu: true },
  { id: 'mz-rerun-check',              label: 'MZ: Re-run Check',              key: 'rerun-check', contextMenu: true },
  { id: 'mz-explain-issue',            label: 'MZ: Explain Issue',             key: 'explain-issue'            },
  { id: 'mz-jump-to-end-of-file',      label: 'MZ: Jump to End of File',       key: 'jump-to-end-of-file'      },
  { id: 'mz-quick-check',              label: 'MZ: Quick Check',               key: 'quick-check'                   },
]

const CodeEditorView = forwardRef(function CodeEditorView({ onBack, source = 'analysis', nodeType, analysisContext, hideHeader = false, hideEditorToolbar = false, externalNavTarget = null, activeJavaFile = null, onManualEditSave, onDirtyChange, editorScope = 'focused', onScopeChange, onCodeCheck = null, upstreamJobId = null, upstreamJobType = null, paletteCommandHandlers = null, gateIssues = null }, ref) {
  // Get current application from context
  const { currentApplication, loading: appLoading } = useApplications()

  // Get editor settings from user preferences
  const { preferences } = useSettings()
  const editorOptions = useMemo(() => ({
    fontSize: preferences?.fontSize || 14,
    fontFamily: preferences?.editorSettings?.fontFamily || "'JetBrains Mono', 'Fira Code', Consolas, monospace",
    minimap: { enabled: preferences?.editorSettings?.minimap ?? true },
    lineNumbers: preferences?.editorSettings?.lineNumbers !== false ? 'on' : 'off',
    wordWrap: preferences?.editorSettings?.wordWrap ? 'on' : 'off',
    scrollBeyondLastLine: false,
    automaticLayout: true,
    glyphMargin: true,
    folding: true,
    renderLineHighlight: 'all',
  }), [preferences])

  // Determine if we're showing refactored, service-generated, compile failure, or package files
  const isRefactorSource = source === 'refactor'
  const isServiceGenSource = source === 'service-generation'
  const isCompileSource = source === 'compile'
  const isPackageSource = source === 'package'

  // Extract job IDs from analysisContext (accumulated from all workflow nodes)
  const ingestJobId = analysisContext?.jobs?.ingest_job_id
  const codeAnalysisJobId = analysisContext?.jobs?.codeanalysis_job_id
  const codeRefactorJobId = analysisContext?.jobs?.coderefactor_job_id
  const serviceGenerationJobId = analysisContext?.jobs?.service_generation_job_id
  const preFlightJobId = analysisContext?.jobs?.pre_flight_job_id
  const javaPackageJobId = analysisContext?.jobs?.java_packaging_job_id

  // Determine which job produces the Java files shown in the right panel.
  // - package: use java_packaging job → compile_failure_sources/*.java
  // - compile: use pre_flight job → compile_failure_sources/*.java
  // - refactor: use code_refactor job → transformed/*.java (via manifest catalog at node path)
  // - service-generation: use service_generation job → services/*.java + entities/*.java
  // - analysis (default): use code_analysis job → generated/*.java
  const javaJobId = isPackageSource ? javaPackageJobId :
                    isCompileSource ? preFlightJobId :
                    isRefactorSource ? codeRefactorJobId :
                    isServiceGenSource ? serviceGenerationJobId :
                    codeAnalysisJobId

  // Sections ALWAYS come from Code Analysis job (only code_analysis produces procedure_model.json)
  const sectionsJobId = codeAnalysisJobId

  if (DEBUG_REFACTOR) {
    console.log('[CodeEditorView] analysisContext:', analysisContext)
    console.log('[CodeEditorView] ingestJobId:', ingestJobId)
    console.log('[CodeEditorView] javaJobId:', javaJobId, '(source:', source, ')')
    console.log('[CodeEditorView] sectionsJobId:', sectionsJobId, '(ALWAYS code_analysis)')
  }

  // Get files from the Files API
  const {
    projectFiles,
    sections: apiSections,
    loading: filesLoading,
    listLoading,            // true while bundle fetch is in-flight
    error: filesError,
    isReady,
    readCobolFile,
    readJavaFile,
    saveJavaFile,
    deleteJavaFileOverlay,
  } = useCodeEditorFiles(currentApplication, null, source, ingestJobId, javaJobId, sectionsJobId, preFlightJobId, javaPackageJobId, editorScope, upstreamJobId, upstreamJobType)

  // Full-scope file filter
  const [fileFilter, setFileFilter] = useState('')
  // Reset filter when scope changes
  useEffect(() => { setFileFilter('') }, [editorScope])

  // Filtered Java file list (only applies in full mode)
  const displayedJavaFiles = useMemo(() => {
    if (!fileFilter.trim() || editorScope !== 'full') return projectFiles.java
    const q = fileFilter.toLowerCase()
    return projectFiles.java.filter(f => f.name.toLowerCase().includes(q))
  }, [projectFiles.java, fileFilter, editorScope])

  // FIX: tab-editor mismatch — 2026-03-17 — CASE B
  // Guard flag: once the user has explicitly selected a file (via tab click or
  // externalNavTarget), suppress the auto-select-first-file effect so it cannot
  // clobber back to java[0] when projectFiles refreshes.
  const userHasSelectedFileRef = useRef(false)

  // Selected file IDs
  const [selectedSourceFileId, setSelectedSourceFileId] = useState(null)
  const [selectedTargetFileId, setSelectedTargetFileId] = useState(null)

  // File content (keyed by file ID for caching edits)
  const [sourceFilesContent, setSourceFilesContent] = useState({})
  const [targetFilesContent, setTargetFilesContent] = useState({})

  // Get current file content
  const sourceContent = sourceFilesContent[selectedSourceFileId] || ''
  const targetContent = targetFilesContent[selectedTargetFileId] || ''

  // Get current file info
  const currentSourceFile = projectFiles.cobol.find(f => f.id === selectedSourceFileId)
  const currentTargetFile = projectFiles.java.find(f => f.id === selectedTargetFileId)

  // Per-pane loading: true when a file is selected but its content isn't cached yet.
  // Avoids blank Monaco pane during async fetch — show loading indicator instead.
  // Note: render checks javaLoadError before targetLoading, so the error state always takes
  // priority in the Java pane without needing !javaLoadError here (avoids TDZ issue).
  const sourceLoading = !!(selectedSourceFileId && currentSourceFile && !sourceFilesContent[selectedSourceFileId])
  const targetLoading = !!(selectedTargetFileId && currentTargetFile && !targetFilesContent[selectedTargetFileId])

  // Update target content
  const setTargetContent = useCallback((content) => {
    setTargetFilesContent(prev => ({
      ...prev,
      [selectedTargetFileId]: content
    }))
  }, [selectedTargetFileId])

  // Sections state (with conversion status)
  const [sections, setSections] = useState([])
  const [selectedSectionId, setSelectedSectionId] = useState(null)
  const [sectionsPanelCollapsed, setSectionsPanelCollapsed] = useState(
    () => localStorage.getItem('cev_sections_collapsed') === 'true'
  )
  const [cobolPanelCollapsed, setCobolPanelCollapsed] = useState(
    () => localStorage.getItem('cev_cobol_collapsed') === 'true'
  )

  // Persist collapse states to localStorage
  useEffect(() => {
    localStorage.setItem('cev_sections_collapsed', sectionsPanelCollapsed)
  }, [sectionsPanelCollapsed])
  useEffect(() => {
    localStorage.setItem('cev_cobol_collapsed', cobolPanelCollapsed)
  }, [cobolPanelCollapsed])

  // Update sections from API
  useEffect(() => {
    if (apiSections?.length > 0) {
      setSections(apiSections)
    }
  }, [apiSections])

  // Auto-select first files when loaded
  // FIX: tab-editor mismatch — 2026-03-17 — CASE B
  // Guard: if the user has already made an explicit file selection (via tab click or
  // externalNavTarget), skip the auto-select so it cannot clobber back to [0].
  useEffect(() => {
    if (isReady && !selectedSourceFileId && projectFiles.cobol.length > 0) {
      setSelectedSourceFileId(projectFiles.cobol[0].id)
    }
    if (isReady && !selectedTargetFileId && projectFiles.java.length > 0 && !userHasSelectedFileRef.current) {
      if (import.meta.env.DEV) console.log(' [FILE-SELECT]', { newId: projectFiles.java[0].id, label: 'auto-select-first', ts: Date.now() })
      setSelectedTargetFileId(projectFiles.java[0].id)
    }
  }, [isReady, projectFiles, selectedSourceFileId, selectedTargetFileId])

  // Load COBOL file content when source file changes
  useEffect(() => {
    const loadSourceFile = async () => {
      if (!selectedSourceFileId || !currentSourceFile || sourceFilesContent[selectedSourceFileId]) return

      try {
        const content = await readCobolFile(currentSourceFile.path)
        setSourceFilesContent(prev => ({
          ...prev,
          [selectedSourceFileId]: content
        }))
      } catch (err) {
        console.error('Failed to load COBOL file:', err)
      }
    }

    loadSourceFile()
  }, [selectedSourceFileId, currentSourceFile, readCobolFile, sourceFilesContent])

  // Load Java file content when target file changes
  useEffect(() => {
    const loadTargetFile = async () => {
      if (!selectedTargetFileId || !currentTargetFile || targetFilesContent[selectedTargetFileId]) return

      // Clear any previous error when a new file is selected
      setJavaLoadError(null)

      if (DEBUG_REFACTOR) {
        console.log('[CodeEditorView] Loading Java file:', {
          name: currentTargetFile.name,
          path: currentTargetFile.path,
          job_id: currentTargetFile.job_id,   // per-file job_id from bundle
          stage: currentTargetFile.stage
        })
      }

      try {
        // Pass currentTargetFile as fileMetadata so readJavaFile uses the file's own job_id
        // (stored in bundle response) rather than falling back to the global javaJobId.
        const content = await readJavaFile(currentTargetFile.path, currentTargetFile)
        if (DEBUG_REFACTOR) console.log('[CodeEditorView] Java file loaded, bytes:', content?.length)
        if (import.meta.env.DEV) console.log(' [CONTENT-SET]', { fileId: selectedTargetFileId, preview: content?.slice(0, 40), ts: Date.now() })
        setTargetFilesContent(prev => ({
          ...prev,
          [selectedTargetFileId]: content
        }))
      } catch (err) {
        console.error('Failed to load Java file:', err)
        setJavaLoadError(`Failed to load Java: ${err.message}`)
      }
    }

    loadTargetFile()
  }, [selectedTargetFileId, currentTargetFile, readJavaFile, targetFilesContent])

  // AI modal state
  const [aiModal, setAiModal] = useState({
    isOpen: false,
    mode: 'explain',
    title: '',
    selectedCode: '',
    sectionName: ''
  })
  const [aiResponse, setAiResponse] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)

  // Inline Java load error (shown in right panel when content fails to load)
  const [javaLoadError, setJavaLoadError] = useState(null)

  // AI Clean Code state
  const [cleanCodeModal, setCleanCodeModal] = useState(null)
  const [cleanCodeLoading, setCleanCodeLoading] = useState(false)
  const handleCleanCodeRef = useRef(null)
  const handleDownloadSourceRef = useRef(null)
  const handleDownloadTargetRef = useRef(null)
  const handleStartFreshRef = useRef(null)
  // Stable ref for MZ: palette handlers — updated each render, avoids dispose/re-register cycle
  const paletteCommandHandlerRef = useRef(null)
  useEffect(() => { paletteCommandHandlerRef.current = paletteCommandHandlers }, [paletteCommandHandlers])

  // Stores the onAccept callback provided by the caller of triggerAiAction.
  // Cleared after first use so it does not fire on subsequent modal opens.
  const onAcceptCallbackRef = useRef(null)

  // Editor refs
  const [sourceEditor, setSourceEditor] = useState(null)
  const [targetEditor, setTargetEditor] = useState(null)
  const sourceEditorRef = useRef(null)
  const decorationsRef = useRef([])
  const javaDecorationsRef = useRef([])
  // Pending nav: stores a javaFile basename when externalNavTarget fired before the bundle loaded.
  // Cleared when the retry effect (below) resolves it.
  const pendingJavaNavRef = useRef(null)

  // ── Monaco markers (red squiggles) for BLOCKER gate issues ───────────────
  // Translates gateIssues → Monaco model markers for the currently loaded Java file.
  // Runs whenever the editor, issues, or active file changes.
  useEffect(() => {
    if (!targetEditor || !window.monaco) return
    const monaco = window.monaco
    const model = targetEditor.getModel()
    if (!model) return

    const markers = []
    if (gateIssues?.length && currentTargetFile?.name) {
      const fileName = currentTargetFile.name
      for (const issue of gateIssues) {
        const issueFile = issue.javaLocation?.file
        if (!issueFile) continue
        // Match by basename (handles both "PayrollService.java" and full paths)
        const issueBasename = issueFile.split('/').pop()
        if (issueBasename !== fileName && issueFile !== fileName) continue
        const line = issue.javaLocation?.line || 1
        const severity = issue.severity === 'BLOCK'
          ? monaco.MarkerSeverity.Error
          : monaco.MarkerSeverity.Warning
        markers.push({
          severity,
          startLineNumber: line,
          startColumn:     1,
          endLineNumber:   line,
          endColumn:       model.getLineMaxColumn(line),
          message:         issue.summary || 'Gate issue',
          source:          'RecodeIT',
        })
      }
    }
    monaco.editor.setModelMarkers(model, 'mz-gate-issues', markers)
    if (import.meta.env.DEV) {
      console.log(` [CodeEditorView] setModelMarkers: ${markers.length} markers for ${currentTargetFile?.name || '?'}`)
    }
  }, [targetEditor, gateIssues, currentTargetFile])

  // State for editor-based AI (when user selects code in editor)
  const [editorSelection, setEditorSelection] = useState(null)

  // Get selected section object
  const selectedSection = useMemo(() => {
    return sections.find(s => s.id === selectedSectionId) || null
  }, [sections, selectedSectionId])

  // Calculate progress
  const progress = useMemo(() => {
    const completed = sections.filter(s => s.status === 'converted').length
    return { completed, total: sections.length }
  }, [sections])

  // Get section code
  const getSectionCode = useCallback((sectionId) => {
    const section = sections.find(s => s.id === sectionId)
    if (!section) return ''
    const lines = sourceContent.split('\n')
    return lines.slice(section.startLine - 1, section.endLine).join('\n')
  }, [sections, sourceContent])

  // Handle section selection
  const handleSelectSection = useCallback((sectionId) => {
    setSelectedSectionId(sectionId)

    // Find the section and scroll to it in source editor
    const section = sections.find(s => s.id === sectionId)
    if (section && sourceEditor) {
      // Get Monaco instance
      const monaco = window.monaco

      // Clear previous decorations
      if (decorationsRef.current.length > 0) {
        decorationsRef.current = sourceEditor.deltaDecorations(decorationsRef.current, [])
      }

      // Scroll to the section
      sourceEditor.revealLineInCenter(section.startLine)

      // Create new decorations for highlighting
      decorationsRef.current = sourceEditor.deltaDecorations([], [
        {
          range: new monaco.Range(section.startLine, 1, section.endLine, 1000),
          options: {
            isWholeLine: true,
            className: 'selected-section-highlight',
            glyphMarginClassName: 'selected-section-glyph',
            overviewRuler: {
              color: '#4a90e2',
              position: monaco.editor.OverviewRulerLane.Full
            }
          }
        }
      ])

      // Also select the range so it's visually highlighted
      sourceEditor.setSelection(new monaco.Range(section.startLine, 1, section.endLine, 1000))
    }
  }, [sections, sourceEditor])

  // Handle regenerate (re-run the current AI action)
  const handleRegenerate = useCallback(() => {
    if (aiModal.mode && aiModal.selectedCode) {
      // Re-trigger the AI call with current modal content
      setAiLoading(true)
      setAiResponse(null)

      const callAi = async () => {
        try {
          let response
          switch (aiModal.mode) {
            case 'generate':
              response = await generateJavaFromCobol(aiModal.selectedCode, 'regenerate', aiModal.sectionName)
              break
            case 'explain':
              response = await explainCobolCode(aiModal.selectedCode, 'regenerate', aiModal.sectionName)
              break
            case 'optimize':
              response = await optimizeJavaCode(aiModal.selectedCode, 'regenerate', aiModal.sectionName)
              break
            case 'custom':
              response = await customPromptAI(aiModal.selectedCode, 'Analyze this code', 'regenerate', aiModal.sectionName)
              break
            default:
              response = await explainCobolCode(aiModal.selectedCode, 'regenerate', aiModal.sectionName)
          }
          setAiResponse(response)
        } catch (error) {
          console.error('AI error:', error)
          setAiResponse({ error: error.message })
        } finally {
          setAiLoading(false)
        }
      }
      callAi()
    }
  }, [aiModal])

  // Handle accept generated code - insert at cursor position in target editor
  const handleAcceptCode = useCallback((code) => {
    if (!targetEditor || !code) return

    const monaco = window.monaco
    const position = targetEditor.getPosition()
    const selection = targetEditor.getSelection()

    // If there's a selection, replace it. Otherwise insert at cursor.
    const range = selection && !selection.isEmpty()
      ? selection
      : new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column)

    // Add newlines for clean insertion
    const codeToInsert = '\n' + code + '\n'

    // Execute the edit
    targetEditor.executeEdits('ai-insert', [{
      range: range,
      text: codeToInsert,
      forceMoveMarkers: true
    }])

    // Move cursor to end of inserted code
    const newPosition = targetEditor.getPosition()
    targetEditor.setPosition(newPosition)
    targetEditor.focus()

    // Update section status if we have a selected section
    if (selectedSection) {
      setSections(prev => prev.map(s =>
        s.id === selectedSection.id ? { ...s, status: 'converted' } : s
      ))
    }

    setAiModal(prev => ({ ...prev, isOpen: false }))
    setAiResponse(null)

    // Notify caller (e.g. ResolveIssuesView) so it can persist a GateDecision
    if (onAcceptCallbackRef.current) {
      onAcceptCallbackRef.current(code, position.lineNumber)
      onAcceptCallbackRef.current = null
    }
  }, [targetEditor, selectedSection])

  // Handle close AI modal
  const handleCloseAiModal = useCallback(() => {
    setAiModal(prev => ({ ...prev, isOpen: false }))
    setAiResponse(null)
  }, [])

  // Handle custom prompt submission from modal
  const handleCustomPromptSubmit = useCallback((userPrompt) => {
    if (!aiModal.selectedCode || !userPrompt) return

    setAiLoading(true)
    setAiResponse(null)

    const callAi = async () => {
      try {
        const response = await customPromptAI(
          aiModal.selectedCode,
          userPrompt,
          'custom-prompt',
          aiModal.sectionName
        )
        setAiResponse(response)
      } catch (error) {
        console.error('AI error:', error)
        setAiResponse({ error: error.message })
      } finally {
        setAiLoading(false)
      }
    }
    callAi()
  }, [aiModal.selectedCode, aiModal.sectionName])

  // Handle start fresh - delete overlay and reload original Java from job artifacts
  // (Option D: job-scoped, delete user_edits/{file} overlay only)
  const handleStartFresh = useCallback(async () => {
    if (!currentTargetFile) {
      alert('No Java file selected. Please select a Java file first.')
      return
    }

    // Confirmation prompt before resetting
    const confirmed = confirm(
      `Reset this file to the original generated code?\n\n` +
      `Your unsaved changes will be lost for:\n${currentTargetFile.name}\n\n` +
      `Click OK to reset, or Cancel to keep your changes.`
    )

    if (!confirmed) {
      console.log(' [START_FRESH] User cancelled reset')
      return
    }

    try {
      console.log(' [START_FRESH] Deleting overlay for:', currentTargetFile.path)

      // Delete overlay (user_edits/{file})
      const result = await deleteJavaFileOverlay(currentTargetFile.path)

      console.log(' [START_FRESH] Delete result:', result)

      // Reload Java file content (will now read from original since overlay is deleted)
      const freshContent = await readJavaFile(currentTargetFile.path, currentTargetFile)

      // Update editor buffer with original content
      setTargetContent(freshContent)

      console.log(' [START_FRESH] Java file reset to original from job_id:', javaJobId)

      // Success message
      const message = result.deleted
        ? `✅ Reset complete\n\nFile reverted to original generated code:\n${currentTargetFile.name}`
        : `ℹ️ Already using original\n\nNo edits existed for:\n${currentTargetFile.name}`

      alert(message)
    } catch (err) {
      console.error(' [START_FRESH] Failed to reset Java file:', err)
      alert(`❌ Failed to reset file\n\n${err.message}\n\nFile: ${currentTargetFile.path}`)
    }
  }, [currentTargetFile, deleteJavaFileOverlay, readJavaFile, setTargetContent, javaJobId])

  handleStartFreshRef.current = handleStartFresh

  // ── Dirty state tracking — must be declared before handleSave (TDZ guard) ──
  // lastSavedContent: null = file not yet loaded; string = content at last save (or on load)
  const [lastSavedContent, setLastSavedContent] = useState(null)
  const [saveStatus, setSaveStatus]             = useState('idle') // idle|saving|saved|error
  const lastFileIdRef = useRef(null)
  const handleSaveRef = useRef(null)  // stable ref (same pattern as handleCleanCodeRef)
  const prevDirtyRef  = useRef(false)

  // Handle save
  const handleSave = useCallback(async () => {
    if (!currentTargetFile) {
      console.warn('No target file selected')
      return
    }

    const prevContent = lastSavedContent
    setSaveStatus('saving')
    try {
      const result = await saveJavaFile(currentTargetFile.path, targetContent)
      console.log(' [SAVE] File saved:', currentTargetFile.path, '→', result.uri)

      // Notify parent of changed ranges for GateDecision persistence
      if (onManualEditSave && prevContent !== null && prevContent !== targetContent) {
        const changedRanges = computeChangedRanges(prevContent, targetContent)
        if (changedRanges.length) onManualEditSave(changedRanges, targetContent, currentTargetFile)
      }

      setLastSavedContent(targetContent)
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (err) {
      console.error(' [SAVE] Failed to save file:', err)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 5000)
    }
  }, [targetContent, saveJavaFile, currentTargetFile, lastSavedContent, onManualEditSave])

  handleSaveRef.current = handleSave

  // Handle download source (COBOL) file
  const handleDownloadSource = useCallback(() => {
    if (!currentSourceFile || !sourceContent) {
      console.warn('No source file to download')
      return
    }

    try {
      const blob = new Blob([sourceContent], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = currentSourceFile.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      console.log('Downloaded source file:', currentSourceFile.name)
    } catch (err) {
      console.error('Failed to download source file:', err)
      alert('Failed to download file: ' + err.message)
    }
  }, [currentSourceFile, sourceContent])

  handleDownloadSourceRef.current = handleDownloadSource

  // Handle download target (Java) file
  const handleDownloadTarget = useCallback(() => {
    if (!currentTargetFile || !targetContent) {
      console.warn('No target file to download')
      return
    }

    try {
      const blob = new Blob([targetContent], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = currentTargetFile.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      console.log('Downloaded target file:', currentTargetFile.name)
    } catch (err) {
      console.error('Failed to download target file:', err)
      alert('Failed to download file: ' + err.message)
    }
  }, [currentTargetFile, targetContent])

  handleDownloadTargetRef.current = handleDownloadTarget

  // Handle AI action from editor selection
  const handleEditorAiAction = useCallback((action, editor) => {
    const selection = editor.getSelection()
    const model = editor.getModel()

    if (!selection || selection.isEmpty()) {
      // No selection, get current line
      const lineNumber = selection.startLineNumber
      const lineContent = model.getLineContent(lineNumber)
      setEditorSelection({
        code: lineContent,
        startLine: lineNumber,
        endLine: lineNumber
      })
    } else {
      // Get selected text
      const selectedText = model.getValueInRange(selection)
      setEditorSelection({
        code: selectedText,
        startLine: selection.startLineNumber,
        endLine: selection.endLineNumber
      })
    }

    // Find which section this belongs to (if any)
    const lineNum = selection.startLineNumber
    const matchingSection = sections.find(s =>
      lineNum >= s.startLine && lineNum <= s.endLine
    )

    const sectionName = matchingSection?.displayName || `Lines ${selection.startLineNumber}-${selection.endLineNumber}`

    // Set up modal
    const titles = {
      generate: 'Generate Java Code',
      explain: 'AI Code Explanation',
      optimize: 'Refactoring Suggestions',
      custom: 'Custom AI Analysis'
    }

    const selectedText = selection.isEmpty()
      ? model.getLineContent(selection.startLineNumber)
      : model.getValueInRange(selection)

    setAiModal({
      isOpen: true,
      mode: action,
      title: titles[action] || 'AI Analysis',
      selectedCode: selectedText,
      sectionName: sectionName
    })

    // For custom mode, don't auto-call AI - wait for user to enter prompt
    if (action === 'custom') {
      setAiLoading(false)
      setAiResponse(null)
      return
    }

    // Start AI generation for non-custom actions
    setAiLoading(true)
    setAiResponse(null)

    // Call AI service
    const callAi = async () => {
      try {
        let response
        switch (action) {
          case 'generate':
            response = await generateJavaFromCobol(selectedText, 'editor-selection', sectionName)
            break
          case 'explain':
            response = await explainCobolCode(selectedText, 'editor-selection', sectionName)
            break
          case 'optimize':
            response = await optimizeJavaCode(selectedText, 'editor-selection', sectionName)
            break
          default:
            response = await explainCobolCode(selectedText, 'editor-selection', sectionName)
        }
        setAiResponse(response)
      } catch (error) {
        console.error('AI error:', error)
        setAiResponse({ error: error.message })
      } finally {
        setAiLoading(false)
      }
    }
    callAi()
  }, [sections])

  // AI Clean Code — reads current Java editor content, calls backend, shows diff modal
  const handleCleanCode = useCallback(async () => {
    const content = targetEditor?.getValue() ?? targetContent
    const filename = currentTargetFile?.name || 'file.java'
    if (!content || !content.trim()) return

    setCleanCodeLoading(true)
    try {
      const result = await cleanCode(content, 'Java', filename)
      if (!result.cleaned || result.cleaned.trim() === content.trim()) {
        alert('AI Clean Code: no formatting changes detected.')
        return
      }
      setCleanCodeModal({ isOpen: true, original: content, cleaned: result.cleaned, filename, language: 'java' })
    } catch (err) {
      alert(`AI Clean Code failed: ${err.message}`)
    } finally {
      setCleanCodeLoading(false)
    }
  }, [targetEditor, targetContent, currentTargetFile])

  // Must be set after handleCleanCode is defined — lets the Monaco action always call the latest handler
  handleCleanCodeRef.current = handleCleanCode

  // Reset when file switches
  useEffect(() => {
    if (selectedTargetFileId !== lastFileIdRef.current) {
      lastFileIdRef.current = selectedTargetFileId
      setLastSavedContent(null)
      setSaveStatus('idle')
    }
  }, [selectedTargetFileId])

  // Initialize once content arrives (file first load)
  useEffect(() => {
    if (lastSavedContent === null && targetContent && !targetLoading) {
      setLastSavedContent(targetContent)
    }
  }, [targetContent, targetLoading, lastSavedContent])

  const isDirty = lastSavedContent !== null && targetContent !== lastSavedContent

  // Notify parent of dirty state changes
  useEffect(() => {
    if (isDirty !== prevDirtyRef.current) {
      prevDirtyRef.current = isDirty
      onDirtyChange?.(isDirty)
    }
  }, [isDirty, onDirtyChange])

  // ── Embedded editor API (used by ResolveIssuesView via forwardRef) ────────────────
  // Expose triggerAiAction so the Fix Panel's "Ask AI" button can call the same
  // handler that Monaco's command palette uses — no separate AI path needed.
  // onAccept is an optional callback: (code: string, lineNumber: number) => void.
  // It is stored in onAcceptCallbackRef and called once when the user accepts AI code.
  useImperativeHandle(ref, () => ({
    triggerAiAction: (action, onAccept) => {
      if (onAccept) onAcceptCallbackRef.current = onAccept
      if (targetEditor) handleEditorAiAction(action, targetEditor)
    },
    focusEditor: () => targetEditor?.focus(),
    // Insert code at a specific line in the Java (target) editor.
    // Used by ResolveIssuesView AI panel "Apply" to insert AI-suggested fixes.
    insertCodeAtLine: (code, lineNumber) => {
      if (!targetEditor) return
      const monaco = window.monaco
      if (!monaco) return
      const range = new monaco.Range(lineNumber, 1, lineNumber, 1000)
      targetEditor.executeEdits('ai-fix-apply', [{
        range,
        text: code,
        forceMoveMarkers: true,
      }])
      targetEditor.revealLineInCenter(lineNumber)
      targetEditor.focus()
    },
    // Get code around a line in the Java (target) editor for diff context.
    // Returns { text, startLine, endLine } or null if editor not available.
    getCodeAroundLine: (lineNumber, contextLines = 5) => {
      if (!targetEditor) return null
      const model = targetEditor.getModel()
      if (!model) return null
      const totalLines = model.getLineCount()
      const startLine = Math.max(1, lineNumber - contextLines)
      const endLine = Math.min(totalLines, lineNumber + contextLines)
      const lines = []
      for (let i = startLine; i <= endLine; i++) {
        lines.push(model.getLineContent(i))
      }
      return { text: lines.join('\n'), startLine, endLine }
    },
    // ── Manual edit support ──────────────────────────────────────────────
    // Trigger the save handler from parent (e.g. "Save & Re-run" modal).
    triggerSave: () => handleSaveRef.current?.(),
    // Current selection in the Java editor — used for block comment action.
    getSelectedRange: () => {
      const sel = targetEditor?.getSelection()
      if (!sel) return null
      return {
        startLine: sel.startLineNumber,
        endLine:   sel.endLineNumber,
        startCol:  sel.startColumn,
        endCol:    sel.endColumn,
        isEmpty:   sel.isEmpty(),
      }
    },
    // Full current file content from Monaco model (always live, no stale closure).
    getFileContent: () => targetEditor?.getModel()?.getValue() ?? '',
    // Replace a range of lines with newText — used for block comment.
    replaceLineRange: (startLine, endLine, newText) => {
      if (!targetEditor || !window.monaco) return
      const range = new window.monaco.Range(startLine, 1, endLine, 1000)
      targetEditor.executeEdits('manual-replace', [{
        range,
        text: newText,
        forceMoveMarkers: true,
      }])
      targetEditor.revealLineInCenter(startLine)
      targetEditor.focus()
    },
    // Scroll Java editor to the last line — used by Manual Fix Playbook.
    jumpToEndOfFile: () => {
      if (!targetEditor) return
      const model = targetEditor.getModel()
      if (!model) return
      const lastLine = model.getLineCount()
      targetEditor.revealLine(lastLine)
      targetEditor.setPosition({ lineNumber: lastLine, column: model.getLineMaxColumn(lastLine) })
      targetEditor.focus()
    },
    // ── ServiceGenerationView action triggers (ref-stable, no stale closures) ─
    triggerDownloadSource: () => handleDownloadSourceRef.current?.(),
    triggerDownloadTarget: () => handleDownloadTargetRef.current?.(),
    triggerStartFresh:     () => handleStartFreshRef.current?.(),
    triggerCleanCode:      () => handleCleanCodeRef.current?.(),
  }), [targetEditor, handleEditorAiAction])

  // ── External navigation (driven by ResolveIssuesView issue selection) ────────────
  // When externalNavTarget changes, switch to the issue's COBOL + Java files and
  // scroll the COBOL editor to the relevant line.  File matching is by filename.
  useEffect(() => {
    if (!externalNavTarget) {
      // Clear any lingering issue highlight when nav target is cleared
      if (javaDecorationsRef.current.length > 0 && targetEditor) {
        javaDecorationsRef.current = targetEditor.deltaDecorations(javaDecorationsRef.current, [])
      }
      pendingJavaNavRef.current = null
      return
    }

    if (externalNavTarget.cobolFile) {
      const cobolFile = projectFiles.cobol.find(f =>
        f.name === externalNavTarget.cobolFile ||
        f.path?.endsWith('/' + externalNavTarget.cobolFile) ||
        f.path?.includes(externalNavTarget.cobolFile)
      )
      if (cobolFile) setSelectedSourceFileId(cobolFile.id)
    }

    if (externalNavTarget.javaFile) {
      const javaFile = projectFiles.java.find(f =>
        f.name === externalNavTarget.javaFile ||
        f.path === externalNavTarget.javaFile ||
        f.path?.endsWith('/' + externalNavTarget.javaFile) ||
        f.path?.includes(externalNavTarget.javaFile)
      )
      if (import.meta.env.DEV) console.log(' [NAV-TARGET]', { target: externalNavTarget, matchedFile: javaFile?.id || 'NO MATCH', ts: Date.now() })
      if (javaFile) {
        // FIX: tab-editor mismatch — 2026-03-17 — CASE B
        userHasSelectedFileRef.current = true
        if (import.meta.env.DEV) console.log(' [FILE-SELECT]', { newId: javaFile.id, label: 'externalNavTarget', ts: Date.now() })
        setSelectedTargetFileId(javaFile.id)
        pendingJavaNavRef.current = null
      } else {
        // Bundle not yet loaded — remember this filename for the retry effect below
        pendingJavaNavRef.current = externalNavTarget.javaFile
      }
    }

    const timers = []

    if (externalNavTarget.startLine && sourceEditor) {
      // Delay to allow file content to load before scrolling
      const t = setTimeout(() => {
        console.log(` [CodeEditorView] Scroll COBOL pane → ${externalNavTarget.cobolFile || '?'}:${externalNavTarget.startLine}`)
        sourceEditor.revealLineInCenter(externalNavTarget.startLine)
        const monaco = window.monaco
        if (monaco) {
          const line = externalNavTarget.startLine
          sourceEditor.setSelection(new monaco.Range(line, 1, line, 1000))
        }
      }, 350)
      timers.push(t)
    }

    if (externalNavTarget.javaLine && targetEditor) {
      // Scroll Java (target) editor to the error line — used for compile errors
      // where cobolLocation is null but javaLocation.line is set.
      // Delay increased to 500ms to allow file content to load before scrolling.
      const t = setTimeout(() => {
        const line = externalNavTarget.javaLine
        console.log(` [CodeEditorView] Scroll Java pane → ${externalNavTarget.javaFile || '?'}:${line}`)
        targetEditor.revealLineInCenter(line)
        targetEditor.setPosition({ lineNumber: line, column: 1 })

        // Persistent highlight — stays until next issue is selected (no auto-clear)
        const monaco = window.monaco
        if (monaco) {
          // Clear previous Java decorations (from prior issue selection)
          if (javaDecorationsRef.current.length > 0) {
            javaDecorationsRef.current = targetEditor.deltaDecorations(javaDecorationsRef.current, [])
          }
          javaDecorationsRef.current = targetEditor.deltaDecorations([], [{
            range: new monaco.Range(line, 1, line, 1000),
            options: {
              isWholeLine: true,
              className: 'rv-issue-highlight',
              overviewRuler: { color: '#e24a4a', position: monaco.editor.OverviewRulerLane.Full },
            },
          }])
        }
      }, 500)
      timers.push(t)
    }

    if (timers.length > 0) {
      return () => timers.forEach(clearTimeout)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalNavTarget])

  // ── Retry nav when bundle finishes loading ───────────────────────────────────
  // If externalNavTarget fired before projectFiles.java was populated (race condition),
  // pendingJavaNavRef holds the filename. Re-attempt the lookup whenever the java list changes.
  useEffect(() => {
    const pending = pendingJavaNavRef.current
    if (!pending || !projectFiles.java.length) return
    const javaFile = projectFiles.java.find(f =>
      f.name === pending ||
      f.path?.endsWith('/' + pending) ||
      f.path?.includes(pending)
    )
    if (javaFile) {
      // FIX: tab-editor mismatch — 2026-03-17 — CASE B
      userHasSelectedFileRef.current = true
      if (import.meta.env.DEV) console.log(' [FILE-SELECT]', { newId: javaFile.id, label: 'pending-nav-retry', ts: Date.now() })
      setSelectedTargetFileId(javaFile.id)
      pendingJavaNavRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFiles.java])

  // ── Tab-bar driven file selection ─────────────────────────────────────────
  // activeJavaFile comes from the tab bar in ResolveIssuesView (activeTabPath).
  // When the active tab changes, switch Monaco to that file.
  // Includes projectFiles.java in deps so it retries automatically when the bundle loads.
  // FIX: tab-editor mismatch — 2026-03-17 — CASE B
  useEffect(() => {
    if (import.meta.env.DEV) console.log(' [TAB_SWITCH] activeJavaFile effect fired. activeJavaFile=', activeJavaFile, 'bundle size=', projectFiles.java.length)
    if (!activeJavaFile) return
    if (!projectFiles.java.length) return
    // Support both full paths (src/main/java/.../Foo.java) and bare basenames (Foo.java)
    const basename = activeJavaFile.includes('/') ? activeJavaFile.split('/').pop() : activeJavaFile
    if (import.meta.env.DEV) console.log(' [TAB_SWITCH] looking for basename=', basename, 'in', projectFiles.java.map(f => f.name))
    const javaFile = projectFiles.java.find(f =>
      f.name === basename ||
      f.name === activeJavaFile ||
      f.path === activeJavaFile ||
      f.path?.endsWith('/' + basename)
    )
    if (javaFile) {
      if (import.meta.env.DEV) console.log(' [TAB_SWITCH] FOUND → switching to:', javaFile.name, 'id=', javaFile.id)
      // FIX: tab-editor mismatch — 2026-03-17 — CASE B
      userHasSelectedFileRef.current = true
      if (import.meta.env.DEV) console.log(' [FILE-SELECT]', { newId: javaFile.id, label: 'activeJavaFile-tab-switch', ts: Date.now() })
      setSelectedTargetFileId(javaFile.id)
    } else {
      if (import.meta.env.DEV) console.warn(' [TAB_SWITCH] NOT FOUND in bundle. basename=', basename, 'bundle files=', projectFiles.java.map(f => f.name))
    }
  }, [activeJavaFile, projectFiles.java]) // eslint-disable-line react-hooks/exhaustive-deps

  // Apply cleaned code to the Java editor (preserves undo stack, triggers onChange → setTargetContent)
  const handleApplyCleanCode = useCallback((cleaned) => {
    if (targetEditor) {
      const monaco = window.monaco
      const model = targetEditor.getModel()
      const fullRange = model.getFullModelRange()
      targetEditor.executeEdits('ai-clean-code', [{ range: fullRange, text: cleaned, forceMoveMarkers: true }])
      targetEditor.focus()
    }
    setCleanCodeModal(null)
  }, [targetEditor])

  // Source editor mount handler
  const handleSourceEditorMount = useCallback((editor, monaco) => {
    setSourceEditor(editor)

    // Store monaco globally for later use
    window.monaco = monaco

    // Add AI actions to Command Palette only (no context menu)
    editor.addAction({
      id: 'ai-generate-java',
      label: 'AI: Generate Java Code',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyG],
      run: (ed) => handleEditorAiAction('generate', ed)
    })

    editor.addAction({
      id: 'ai-explain-code',
      label: 'AI: Explain This Code',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyE],
      run: (ed) => handleEditorAiAction('explain', ed)
    })

    editor.addAction({
      id: 'ai-optimize-code',
      label: 'AI: Suggest Refactoring',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyR],
      run: (ed) => handleEditorAiAction('optimize', ed)
    })

    editor.addAction({
      id: 'ai-custom-prompt',
      label: 'AI: Custom Prompt...',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyA],
      run: (ed) => handleEditorAiAction('custom', ed)
    })
  }, [handleEditorAiAction])

  // Target editor mount handler
  const handleTargetEditorMount = useCallback((editor, monaco) => {
    setTargetEditor(editor)

    // Store monaco globally if not already
    if (!window.monaco) {
      window.monaco = monaco
    }

    if (import.meta.env.DEV) {
      console.log(' [CodeEditorView] Java editor mounted')
    }

    // Add AI actions to Java editor (refactoring, explain, custom)
    editor.addAction({
      id: 'ai-explain-java',
      label: 'AI: Explain This Code',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyE],
      run: (ed) => handleEditorAiAction('explain', ed)
    })

    editor.addAction({
      id: 'ai-refactor-java',
      label: 'AI: Suggest Refactoring',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyR],
      run: (ed) => handleEditorAiAction('optimize', ed)
    })

    editor.addAction({
      id: 'ai-custom-java',
      label: 'AI: Custom Prompt...',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyA],
      run: (ed) => handleEditorAiAction('custom', ed)
    })

    editor.addAction({
      id: 'ai-clean-code',
      label: 'AI: Clean Code',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyK],
      run: () => { handleCleanCodeRef.current?.() }
    })

    // MZ: Command Palette opener — Cmd/Ctrl+Shift+P (VS Code-style shortcut)
    editor.addAction({
      id: 'mz.openCommandPalette',
      label: 'MZ: Command Palette',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyP],
      run: (ed) => {
        if (import.meta.env.DEV) {
          console.log(' [CodeEditorView] Cmd+Shift+P → opening command palette')
        }
        ed.trigger('keyboard', 'editor.action.quickCommand', null)
      },
    })

    // Cmd/Ctrl+S → Save (VS Code standard)
    editor.addAction({
      id: 'mz-save-file',
      label: 'MZ: Save File',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
      contextMenuGroupId: 'mz-core',
      contextMenuOrder: 0,
      run: () => handleSaveRef.current?.(),
    })

    // MZ: Fix Panel actions — registered once on mount, always call current ref handler.
    // contextMenu:true entries appear in right-click menu; others are palette-only.
    MZ_PALETTE_COMMANDS.forEach(({ id, label, key, contextMenu }, i) => {
      editor.addAction({
        id,
        label,
        ...(contextMenu ? { contextMenuGroupId: 'mz-core', contextMenuOrder: i + 1 } : {}),
        run: () => paletteCommandHandlerRef.current?.[key]?.(),
      })
    })

    if (import.meta.env.DEV) {
      // Cmd+S + 4 AI actions + 1 palette opener + MZ_PALETTE_COMMANDS.length
      const total = 1 + 4 + 1 + MZ_PALETTE_COMMANDS.length
      console.log(` [CodeEditorView] ${total} actions registered on Java editor`)
    }
  }, [handleEditorAiAction])

  return (
    <div className={`code-editor-view${hideHeader ? ' code-editor-embedded' : ''}`}>
      {/* Header — hidden when embedded inside ResolveIssuesView */}
      {!hideHeader && <div className="editor-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft size={16} />
            <span>Back to Workflow</span>
          </button>
          <div className="file-info">
            {filesLoading && <Loader2 size={14} className="spin" />}
            <FileCode size={16} />
            <span className="file-name">{currentSourceFile?.name || 'Source'}</span>
            <span className="arrow">→</span>
            <FileCode2 size={16} />
            <span className="file-name">{currentTargetFile?.name || 'Target'}</span>
          </div>
        </div>
        <div className="header-right">
          <button
            className="header-btn"
            onClick={handleDownloadSource}
            title="Download Source (COBOL) file"
            disabled={!currentSourceFile || !sourceContent}
          >
            <Download size={16} />
            <span>Download COBOL</span>
          </button>
          <button
            className="header-btn"
            onClick={handleDownloadTarget}
            title="Download Target (Java) file"
            disabled={!currentTargetFile || !targetContent}
          >
            <Download size={16} />
            <span>Download Java</span>
          </button>
          <button className="header-btn" onClick={handleStartFresh} title="Start Fresh">
            <RotateCcw size={16} />
            <span>Start Fresh</span>
          </button>
          <button
            className="header-btn"
            onClick={handleCleanCode}
            disabled={cleanCodeLoading || !currentTargetFile}
            title="AI: Clean Code formatting (Cmd+Shift+K)"
          >
            {cleanCodeLoading ? <Loader2 size={16} className="spin" /> : <Wand2 size={16} />}
            <span>AI Clean Code</span>
          </button>
          <button className="header-btn primary" onClick={handleSave}>
            <Save size={16} />
            <span>Save</span>
          </button>
        </div>
      </div>}

      {/* Main Content - Three panels side by side */}
      <div className="editor-content">
        <Allotment
          key={`cev-${sectionsPanelCollapsed ? 1 : 0}-${cobolPanelCollapsed ? 1 : 0}`}
          defaultSizes={[
            sectionsPanelCollapsed ? 48 : 200,
            cobolPanelCollapsed ? 56 : 400,
            1000 - (sectionsPanelCollapsed ? 48 : 200) - (cobolPanelCollapsed ? 56 : 400),
          ]}
          style={{ width: '100%', height: '100%' }}
        >
          {/* Left: Section Navigator (Collapsible) */}
          <Allotment.Pane
            minSize={sectionsPanelCollapsed ? 48 : 220}
            preferredSize={sectionsPanelCollapsed ? 48 : 280}
            maxSize={sectionsPanelCollapsed ? 48 : 400}
          >
            {sectionsPanelCollapsed ? (
              <div className="sections-collapsed">
                <button
                  className="collapse-toggle"
                  onClick={() => setSectionsPanelCollapsed(false)}
                  title="Expand Sections Panel"
                >
                  <PanelLeft size={18} />
                </button>
                <span className="collapsed-label">Sections</span>
              </div>
            ) : (
              <SectionNavigator
                sections={sections}
                selectedSectionId={selectedSectionId}
                onSelectSection={handleSelectSection}
                progress={progress}
                onCollapse={() => setSectionsPanelCollapsed(true)}
              />
            )}
          </Allotment.Pane>

          {/* Center: Source Code (COBOL) — collapsible */}
          <Allotment.Pane
            minSize={cobolPanelCollapsed ? 56 : 300}
            preferredSize={cobolPanelCollapsed ? 56 : 400}
            maxSize={cobolPanelCollapsed ? 56 : Infinity}
          >
            {cobolPanelCollapsed ? (
              <div className="cobol-collapsed">
                <button
                  className="collapse-toggle"
                  onClick={() => setCobolPanelCollapsed(false)}
                  title="Expand COBOL panel"
                >
                  <PanelLeft size={18} />
                </button>
                <span className="collapsed-label">COBOL</span>
              </div>
            ) : (
              <div className="editor-panel source-panel">
                <div className="panel-header">
                  <span className="panel-title">Source (COBOL)</span>
                  <div className="file-selector">
                    <FileCode size={14} />
                    <select
                      value={selectedSourceFileId || ''}
                      onChange={(e) => setSelectedSourceFileId(e.target.value)}
                      className="file-select"
                      disabled={!isReady || projectFiles.cobol.length === 0}
                    >
                      {projectFiles.cobol.length === 0 ? (
                        <option value="">No files</option>
                      ) : (
                        projectFiles.cobol.map(file => (
                          <option key={file.id} value={file.id}>{file.name}</option>
                        ))
                      )}
                    </select>
                    <ChevronDown size={12} className="select-icon" />
                  </div>
                  <button
                    className="panel-collapse-btn"
                    onClick={() => setCobolPanelCollapsed(true)}
                    title="Collapse COBOL panel"
                  >
                    <PanelLeftClose size={14} />
                  </button>
                </div>
                <div className="panel-editor">
                  {sourceLoading ? (
                    <div className="panel-loading">
                      <Loader2 size={20} className="spinning" />
                      <span>Loading COBOL...</span>
                    </div>
                  ) : (
                    <Editor
                      height="100%"
                      language="plaintext"
                      theme="vs-dark"
                      value={sourceContent}
                      onMount={handleSourceEditorMount}
                      options={{
                        ...editorOptions,
                        readOnly: true,
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </Allotment.Pane>

          {/* Right: Target Code (Java) */}
          <Allotment.Pane minSize={300}>
            <div className="editor-panel target-panel">
              <div className="panel-header">
                {!hideEditorToolbar && (
                  <span className="panel-title">
                    {isRefactorSource    ? 'Refactored (Java)' :
                     isServiceGenSource  ? 'Generated Services (Java)' :
                                          'Target (Java)'}
                  </span>
                )}

                {/* Full-Project scope toggle — only shown when parent provides onScopeChange */}
                {!hideEditorToolbar && onScopeChange && (
                  <div className="editor-scope-tabs">
                    <button
                      className={`editor-scope-tab ${editorScope === 'focused' ? 'active' : ''}`}
                      onClick={() => onScopeChange('focused')}
                      title="Show only gate-relevant files"
                    >
                      Focused
                    </button>
                    <button
                      className={`editor-scope-tab ${editorScope === 'full' ? 'active' : ''}`}
                      onClick={() => onScopeChange('full')}
                      disabled={!upstreamJobId}
                      title={upstreamJobId ? 'Browse full generated Java project' : 'Upstream job not available'}
                    >
                      Full Project
                    </button>
                  </div>
                )}

                {/* Code Checker — fast structural check or compile hint */}
                {!hideEditorToolbar && onCodeCheck && (
                  <div className="editor-scope-tabs editor-code-checker-tabs">
                    <button
                      className="editor-scope-tab"
                      onClick={() => onCodeCheck('fast')}
                      title="Fast structural check: brace balance, package/import order, file-end brace"
                    >
                      Fast Check
                    </button>
                    <button
                      className="editor-scope-tab"
                      onClick={() => onCodeCheck('strict')}
                      title="Full Maven compile check — use Re-run Check in the Fix Panel"
                    >
                      Strict
                    </button>
                  </div>
                )}

                {/* Command Palette opener — fires Monaco's built-in palette */}
                {!hideEditorToolbar && (
                  <button
                    className="editor-palette-btn"
                    onClick={() => {
                      if (targetEditor) {
                        targetEditor.focus()
                        targetEditor.trigger('keyboard', 'editor.action.quickCommand', null)
                      }
                    }}
                    title="Open command palette (Cmd+Shift+P) — search MZ: commands"
                    disabled={!targetEditor}
                  >
                    Palette
                  </button>
                )}

                {!hideEditorToolbar && (
                  <div className="file-selector">
                    <FileCode2 size={14} />
                    <select
                      value={selectedTargetFileId || ''}
                      onChange={(e) => setSelectedTargetFileId(e.target.value)}
                      className="file-select"
                      disabled={!isReady || displayedJavaFiles.length === 0}
                    >
                      {displayedJavaFiles.length === 0 ? (
                        <option value="">
                          {listLoading
                            ? (editorScope === 'full' ? 'Loading full project…' : 'Loading…')
                            : fileFilter ? 'No matches' : 'No files'}
                        </option>
                      ) : (
                        displayedJavaFiles.map(file => (
                          <option key={file.id} value={file.id}>{file.name}</option>
                        ))
                      )}
                    </select>
                    <ChevronDown size={12} className="select-icon" />
                  </div>
                )}

                {/* File filter input — only in full mode */}
                {!hideEditorToolbar && editorScope === 'full' && projectFiles.java.length > 0 && (
                  <input
                    type="text"
                    className="editor-file-filter"
                    placeholder="Filter files…"
                    value={fileFilter}
                    onChange={e => setFileFilter(e.target.value)}
                    spellCheck={false}
                  />
                )}

                {/* File count in full mode */}
                {!hideEditorToolbar && editorScope === 'full' && projectFiles.java.length > 0 && (
                  <span className="editor-full-mode-badge">
                    {fileFilter ? `${displayedJavaFiles.length}/` : ''}{projectFiles.java.length} files
                  </span>
                )}

                {/* Save status indicator — always visible (tab bar uses dirty dot, but save feedback stays) */}
                {isDirty && saveStatus === 'idle' && !hideEditorToolbar && (
                  <span className="editor-dirty-dot" title="Unsaved changes">●</span>
                )}
                {saveStatus !== 'idle' && (
                  <span className={`editor-save-status ${saveStatus}`}>
                    {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? '✓ Saved' : 'Save failed'}
                  </span>
                )}
              </div>

              {/* Full Project file list loading banner */}
              {editorScope === 'full' && listLoading && (
                <div className="editor-scope-loading-banner">
                  <Loader2 size={13} className="spinning" />
                  Loading full project file list — this may take a few seconds…
                </div>
              )}

              <div className="panel-editor">
                {javaLoadError ? (
                  <div className="panel-load-error">
                    <span>⚠ {javaLoadError}</span>
                  </div>
                ) : targetLoading ? (
                  <div className="panel-loading">
                    <Loader2 size={20} className="spinning" />
                    <span>Loading Java...</span>
                  </div>
                ) : (
                  <Editor
                    height="100%"
                    language="java"
                    theme="vs-dark"
                    value={targetContent}
                    onChange={(value) => setTargetContent(value || '')}
                    onMount={handleTargetEditorMount}
                    options={editorOptions}
                  />
                )}
              </div>
            </div>
          </Allotment.Pane>
        </Allotment>
      </div>

      {/* AI Response Modal */}
      <AIResponseModal
        isOpen={aiModal.isOpen}
        onClose={handleCloseAiModal}
        title={aiModal.title}
        mode={aiModal.mode}
        selectedCode={aiModal.selectedCode}
        sectionName={aiModal.sectionName}
        aiResponse={aiResponse}
        aiLoading={aiLoading}
        onRegenerate={handleRegenerate}
        onAccept={handleAcceptCode}
        onCustomPromptSubmit={handleCustomPromptSubmit}
      />

      {/* AI Clean Code diff preview modal */}
      {cleanCodeModal?.isOpen && (
        <CleanCodeModal
          original={cleanCodeModal.original}
          cleaned={cleanCodeModal.cleaned}
          filename={cleanCodeModal.filename}
          language={cleanCodeModal.language}
          onApply={handleApplyCleanCode}
          onClose={() => setCleanCodeModal(null)}
        />
      )}
    </div>
  )
})

export default CodeEditorView
